import React from 'react';
import { FormikRadioInput as Radio } from '@front-activerse/common-components';
import { useTranslator, useAppContext } from '@front-activerse/common-components';
import { useField } from 'formik';
import { Row, Col } from 'reactstrap';

const MACRO_VALUES = {
  standard: { protein: 20, fat: 30, carbohydrate: 50 },
  highProtein: { protein: 30, fat: 40, carbohydrate: 30 },
  lowCarb: { protein: 25, fat: 55, carbohydrate: 20 },
  keto: { protein: 20, fat: 75, carbohydrate: 5 },
};

export const DietType: React.FC = () => {
  const { t } = useTranslator();
  const [field, , helpers] = useField<{ protein: number; fat: number; carbohydrate: number }>(
    'preferredMacronutrientRatio'
  );

  const {
    images: { dietTypeHighProtein, dietTypeKeto, dietTypeLowCarb, dietTypeStandard },
  } = useAppContext();

  const handleRadioChange = (macroKey: string) => {
    const macroValues = MACRO_VALUES[macroKey as keyof typeof MACRO_VALUES];
    helpers.setValue(macroValues);
  };

  const isSelected = (macroKey: string) => {
    if (!field.value) return macroKey === 'standard';
    const macroValues = MACRO_VALUES[macroKey as keyof typeof MACRO_VALUES];
    return (
      field.value.protein === macroValues.protein &&
      field.value.fat === macroValues.fat &&
      field.value.carbohydrate === macroValues.carbohydrate
    );
  };

  return (
    <>
      <h2 className="mb-4 title-large semi-bold">{t('app.nutritionSettings.dietType.headline')}</h2>

      <Radio
        name="preferredMacronutrientRatio"
        value="standard"
        checked={isSelected('standard')}
        onChange={() => handleRadioChange('standard')}
      >
        <img src={dietTypeStandard} className="img-fluid mb-s border-radius-m" alt="dietTypeGlp1" />
        <Row className="mt-m">
          <Col>
            <div className="d-flex align-items-start">
              <div className="bullet protein"></div>
              <div>
                <p className="description-small mb-0">{t('settings.dietType.macros.proteins')}</p>
                <p className="small mb-0">20%</p>
              </div>
            </div>
          </Col>
          <Col>
            <div className="d-flex align-items-start">
              <div className="bullet fat"></div>
              <div>
                <p className="description-small mb-0">{t('settings.dietType.macros.fats')}</p>
                <p className="small mb-0">30%</p>
              </div>
            </div>
          </Col>
          <Col>
            <div className="d-flex align-items-start">
              <div className="bullet carbs"></div>
              <div>
                <p className="description-small mb-0">{t('settings.dietType.macros.carbs')}</p>
                <p className="small mb-0">50%</p>
              </div>
            </div>
          </Col>
        </Row>
      </Radio>

      <Radio
        name="preferredMacronutrientRatio"
        value="highProtein"
        checked={isSelected('highProtein')}
        onChange={() => handleRadioChange('highProtein')}
      >
        <img
          src={dietTypeHighProtein}
          className="img-fluid mb-s border-radius-m"
          alt="dietTypeGlp1"
        />
        <Row className="mt-m">
          <Col>
            <div className="d-flex align-items-start">
              <div className="bullet protein"></div>
              <div>
                <p className="description-small mb-0">{t('settings.dietType.macros.proteins')}</p>
                <p className="small mb-0">30%</p>
              </div>
            </div>
          </Col>
          <Col>
            <div className="d-flex align-items-start">
              <div className="bullet fat"></div>
              <div>
                <p className="description-small mb-0">{t('settings.dietType.macros.fats')}</p>
                <p className="small mb-0">40%</p>
              </div>
            </div>
          </Col>
          <Col>
            <div className="d-flex align-items-start">
              <div className="bullet carbs"></div>
              <div>
                <p className="description-small mb-0">{t('settings.dietType.macros.carbs')}</p>
                <p className="small mb-0">30%</p>
              </div>
            </div>
          </Col>
        </Row>
      </Radio>

      <Radio
        name="preferredMacronutrientRatio"
        value="lowCarb"
        checked={isSelected('lowCarb')}
        onChange={() => handleRadioChange('lowCarb')}
      >
        <img src={dietTypeLowCarb} className="img-fluid mb-s border-radius-m" alt="dietTypeGlp1" />
        <Row className="mt-m">
          <Col>
            <div className="d-flex align-items-start">
              <div className="bullet protein"></div>
              <div>
                <p className="description-small mb-0">{t('settings.dietType.macros.proteins')}</p>
                <p className="small mb-0">25%</p>
              </div>
            </div>
          </Col>
          <Col>
            <div className="d-flex align-items-start">
              <div className="bullet fat"></div>
              <div>
                <p className="description-small mb-0">{t('settings.dietType.macros.fats')}</p>
                <p className="small mb-0">55%</p>
              </div>
            </div>
          </Col>
          <Col>
            <div className="d-flex align-items-start">
              <div className="bullet carbs"></div>
              <div>
                <p className="description-small mb-0">{t('settings.dietType.macros.carbs')}</p>
                <p className="small mb-0">20%</p>
              </div>
            </div>
          </Col>
        </Row>
      </Radio>

      <Radio
        name="preferredMacronutrientRatio"
        value="keto"
        checked={isSelected('keto')}
        onChange={() => handleRadioChange('keto')}
      >
        <img src={dietTypeKeto} className="img-fluid mb-s border-radius-m" alt="dietTypeGlp1" />
        <Row className="mt-m">
          <Col>
            <div className="d-flex align-items-start">
              <div className="bullet protein"></div>
              <div>
                <p className="description-small mb-0">{t('settings.dietType.macros.proteins')}</p>
                <p className="small mb-0">20%</p>
              </div>
            </div>
          </Col>
          <Col>
            <div className="d-flex align-items-start">
              <div className="bullet fat"></div>
              <div>
                <p className="description-small mb-0">{t('settings.dietType.macros.fats')}</p>
                <p className="small mb-0">75%</p>
              </div>
            </div>
          </Col>
          <Col>
            <div className="d-flex align-items-start">
              <div className="bullet carbs"></div>
              <div>
                <p className="description-small mb-0">{t('settings.dietType.macros.carbs')}</p>
                <p className="small mb-0">5%</p>
              </div>
            </div>
          </Col>
        </Row>
      </Radio>
    </>
  );
};

export default DietType;
